import React from 'react';
import { FadeIn, StaggerContainer, StaggerItem } from './FadeIn';
import { ShieldCheckIcon, CodeIcon, NetworkIcon, LandmarkIcon, BookOpenIcon, ArrowUpRightIcon } from 'lucide-react';
interface Founder {
  name: string;
  title: string;
  image?: string;
  initials: string;
  bio: string[];
  highlights: {
    icon: React.ElementType;
    label: string;
  }[];
  book?: {
    title: string;
    url: string;
  };
}
const founders: Founder[] = [{
  name: 'Arthur Miller',
  title: 'Founder, CEO, CTO & CISO',
  image: 'https://cdn.magicpatterns.com/uploads/caDcm14Pxfktf7goeivfVz/image3.png',
  initials: 'AM',
  bio: ['Arthur Miller is a technology executive and enterprise architect with deep experience building secure, large-scale financial infrastructure. His career has focused on modernizing financial systems, designing high-availability distributed platforms, and implementing security architectures for regulated industries.', 'Arthur previously served as a lead engineer working on applications supporting the U.S. Defense Information Systems Agency (DISA) contracted through Tasc (Enginity) and later became an enterprise architect and engineering leader within the financial services industry. He played a key architectural role in the development and scaling of Zelle® and Real-Time Payments (RTP) infrastructure, helping financial institutions adopt modern real-time payment systems at a national scale.', "He later served as Chief Technology Officer of a white-label fintech company focused on Buy-Now-Pay-Later lending solutions, where he led the development of lending infrastructure designed to integrate directly into financial institutions' digital banking ecosystems.", 'Arthur has spent much of his career advising financial institutions and fintech organizations on enterprise architecture, secure integration design, cloud infrastructure, distributed systems, and regulatory-grade security frameworks. His experience across defense systems, national payment networks, and fintech lending platforms uniquely positions him to lead FiQIS in building the next generation of secure financial infrastructure.', 'At FiQIS, Arthur leads company strategy, platform architecture, security design, and regulatory alignment as the organization builds infrastructure that enables financial institutions to modernize safely while maintaining ownership of the customer relationship.'],
  highlights: [{
    icon: ShieldCheckIcon,
    label: 'Ex-DISA & Defense Systems'
  }, {
    icon: LandmarkIcon,
    label: 'Ex-Zelle® & RTP Infrastructure'
  }, {
    icon: NetworkIcon,
    label: 'Expert Enterprise Architect'
  }],
  book: {
    title: 'AI-SDLC: The New Enterprise Architecture',
    url: 'https://a.co/d/0iELdrHA'
  }
}, {
  name: 'Mark Youwanes',
  title: 'Co-Founder & Vice President of Engineering',
  image: 'https://cdn.magicpatterns.com/uploads/fhjUHjqZ4nivpWBMza7DH6/Screenshot_2026-03-09_at_5.20.57_PM.png',
  initials: 'MY',
  bio: ['Mark Youwanes is an experienced software engineering leader specializing in building scalable platforms and managing high-performance engineering teams. His background spans full-stack development, distributed systems, cloud infrastructure, and modern application architecture.', 'Throughout his career, Mark has led engineering teams responsible for designing and deploying complex enterprise platforms that operate at scale. He has extensive experience implementing secure APIs, cloud-native architectures, and resilient microservice ecosystems, enabling organizations to deliver reliable financial technology solutions.', "At FiQIS, Mark oversees platform development and engineering execution. He is responsible for building the company's core technology stack, managing the engineering organization, and ensuring the FiQIS platform meets the performance, scalability, and security requirements expected by regulated financial institutions."],
  highlights: [{
    icon: CodeIcon,
    label: 'Full-Stack & Distributed Systems'
  }, {
    icon: NetworkIcon,
    label: 'Cloud-Native Architecture'
  }, {
    icon: ShieldCheckIcon,
    label: 'Secure API Design'
  }]
}];
export function AboutSection() {
  return <section id="about" className="py-24 bg-navy relative overflow-hidden" data-id="element-13" data-mp="253gf2ejbmxs1">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-teal/3 rounded-full blur-[150px] pointer-events-none" data-id="element-14" data-mp="2055583t10n9t"></div>

      <div className="max-w-7xl mx-auto px-6" data-id="element-15" data-mp="2dyw3uh57ymqr">
        <FadeIn className="mb-20 max-w-3xl" data-id="element-16" data-mp="29wj1m2pol6yq">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium mb-6" data-id="element-17" data-mp="29xu31vvnm2mo">
            Leadership
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-18" data-mp="2iu1pdeuplzeb">
            Built by Engineers Who've Scaled{' '}
            <span className="text-teal" data-id="element-19" data-mp="37h0ykrbsf1v4">National Financial Infrastructure</span>{' '}
            <span className="block mt-2" data-id="element-20" data-mp="37eon8x0jbpr1">
              Led <span className="text-teal" data-id="element-21" data-mp="2siqiiqcjpd37">National Defense R&amp;D Software Teams</span>
            </span>
          </h2>
          <p className="text-xl text-gray-400" data-id="element-22" data-mp="2xha4urv1wqw3">
            Our founding team brings decades of experience from defense systems,
            national payment networks, and enterprise fintech platforms.
          </p>
        </FadeIn>

        <StaggerContainer className="space-y-16" data-id="element-23" data-mp="2ojzwpfdj5hve">
          {founders.map((founder, index) => <StaggerItem key={founder.name} data-id="element-24" data-mp="29rfuqi249gfj" data-mpi={index}>
              <div className="bg-slate/60 border border-white/5 rounded-2xl overflow-hidden" data-id="element-25" data-mp="2etez9v9leje2">
                <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`} data-id="element-26" data-mp="20scctheor72m">
                  {/* Photo / Avatar Column */}
                  <div className="lg:w-[340px] flex-shrink-0 relative" data-id="element-27" data-mp="bbqhh7ffaufj">
                    {founder.image ? <div className="relative h-80 lg:h-full min-h-[400px]" data-id="element-28" data-mp="6jp6rpxwlbrm">
                        <img src={founder.image} alt={founder.name} className="w-full h-full object-cover object-top" data-id="element-29" data-mp="1l45dn6x0m1l" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate/90 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-slate/60" data-id="element-30" data-mp="u902uu4b6hzf"></div>
                      </div> : <div className="h-80 lg:h-full min-h-[400px] bg-gradient-to-br from-slate to-navy flex items-center justify-center relative" data-id="element-31" data-mp="gajvr22japgr">
                        <div className="w-40 h-40 rounded-full bg-teal/10 border-2 border-teal/30 flex items-center justify-center" data-id="element-32" data-mp="3535l0iimj12b">
                          <span className="text-5xl font-bold text-teal" data-id="element-33" data-mp="31358x4izz1yf">
                            {founder.initials}
                          </span>
                        </div>
                        {/* Decorative elements */}
                        <div className="absolute top-12 right-12 w-20 h-20 border border-white/5 rounded-full" data-id="element-34" data-mp="1piwlbbygvccm"></div>
                        <div className="absolute bottom-16 left-10 w-12 h-12 border border-teal/10 rounded-full" data-id="element-35" data-mp="1ub6ind1wveox"></div>
                      </div>}
                  </div>

                  {/* Content Column */}
                  <div className="flex-1 p-8 lg:p-10" data-id="element-36" data-mp="246hqrcitdnnp">
                    <div className="mb-6" data-id="element-37" data-mp="289o577g2yl59">
                      <h3 className="text-3xl font-bold text-white mb-2" data-id="element-38" data-mp="28api7hgnphpm">
                        {founder.name}
                      </h3>
                      <p className="text-teal font-semibold text-lg" data-id="element-39" data-mp="dweq904rm7fm">
                        {founder.title}
                      </p>
                    </div>

                    {/* Highlight Tags */}
                    <div className="flex flex-wrap gap-3 mb-8" data-id="element-40" data-mp="2ks057g3tknir">
                      {founder.highlights.map(h => {
                    const Icon = h.icon;
                    return <div key={h.label} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-navy border border-white/10 text-sm text-gray-300" data-id="element-41" data-mp="2uiuxp27nsjcn">
                            <Icon className="w-4 h-4 text-teal" data-id="element-42" data-mp="21txlngu1jisc" />
                            {h.label}
                          </div>;
                  })}
                    </div>

                    {/* Bio */}
                    <div className="space-y-4" data-id="element-43" data-mp="26s7kq2ivz3j8">
                      {founder.bio.map((paragraph, i) => <p key={i} className="text-gray-400 leading-relaxed text-[15px]" data-id="element-44" data-mp="3n67ib6u9eh3b" data-mpi={i}>
                          {paragraph}
                        </p>)}
                    </div>

                    {/* Published Work */}
                    {founder.book && <a href={founder.book.url} target="_blank" rel="noopener noreferrer" className="group mt-8 flex items-center gap-4 rounded-xl border border-teal/20 bg-navy px-5 py-4 transition-colors duration-200 hover:border-teal/50" data-id="element-45" data-mp="18wv9k21qs93f">
                        <BookOpenIcon className="w-5 h-5 text-teal flex-shrink-0" strokeWidth={1.5} data-id="element-46" data-mp="1rp7jb9uq6cnl" />
                        <div className="min-w-0" data-id="element-47" data-mp="1iwdno1uz10nk">
                          <p className="text-xs font-medium uppercase tracking-widest text-gray-500" data-id="element-48" data-mp="1iv2ka35ftuwz">
                            Author of
                          </p>
                          <p className="text-white font-semibold" data-id="element-49" data-mp="2bf9gkur8gusz">
                            {founder.book.title}
                          </p>
                        </div>
                        <ArrowUpRightIcon className="w-4 h-4 text-gray-500 ml-auto flex-shrink-0 transition-colors duration-200 group-hover:text-teal" data-id="element-50" data-mp="1i34l94isoroh" />
                      </a>}
                  </div>
                </div>
              </div>
            </StaggerItem>)}
        </StaggerContainer>
      </div>
    </section>;
}